import React, { useEffect, useState, useRef } from "react";
import { Stack, Card, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, LinearProgress, ButtonGroup, IconButton, Button, Box, Divider, FilledInput } from "@mui/material";
import { Lesson } from "../../types/Lessons";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { EditableTextField } from "../Shared/FormItems";
import AccessTimeIcon from '@mui/icons-material/AccessTime';

type QuestionaireProps = {
  lesson: Lesson;
  persisted?: boolean;
  multiple?: boolean;
  onCompletion: (scores: Scores) => void;
}

type Question = {
  question: string;
  name: string;
  type: 'radio' | 'text' | 'time';
  options: string[];
  required: boolean;
}

export type Scores = {[key: string]: string|number};

const Questionaire = (props: QuestionaireProps) => {

  const [step, setStep] = useState<number>(1);
  const [name, setName] = useState<string>();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [scores, setScores] = useState<Scores>({});

  const userInteractedRef = useRef(false);

  useEffect(() => {
    var questions: Question[] = [
      { name: "frequency_score", type: "radio", required: true, question: "How frequent does the problem occur?", options: [ "Daily", "Weekly", "Monthly", "Less often", "Only once" ] },
      { name: "impact_score", type: "radio", required: true, question: "How serious is the impact of this problem on the operation or project?", options: [ "Very high (high impact on operations)", "Medium (moderate impact)", "Low (low impact)" ] },
      { name: "recurrence_score", type: "radio", required: true, question: "Do you think this problem will recur in the future if no action is taken?", options: [ "Yes, with high probability", "Yes, with low probability", "No" ] },
    ]
    if (!props.persisted) {
      questions.push(
        { name: "time_lost", type: "time", required: false, question: "What is the estimated time lost (in minutes) caused by this lessons learned?", options: [ "Estimated time lost (in minutes)" ] },
        { name: "origin", type: "text", required: false, question: "Does this issue relate to a certain customer or project?", options: ["Customer or project name"] },
        { name: "mitigation", type: "text", required: false, question: "Is there any mitigation to resolve this issue?", options: ["Describe the result e.g. add a mitigation or insert a link to any useful resource that helps resolving the issue."] }
      );
    }
    setQuestions(questions);
  }, [props.persisted]);

  useEffect(() => {
    setScores(Object.fromEntries(questions.map(question => [question.name, props.lesson[question.name]])));

    if (props.persisted || questions.length <= 0) { return }
    if (Object.values(scores).filter(score => score).length < questions.length) {  return }
    setStep(questions.length + 1);
  }, [questions]);

  useEffect(() => {
    if (step > questions.length && userInteractedRef.current) { 
      setName(undefined);
      props.onCompletion(scores);
      userInteractedRef.current = false;
      return 
    } else if (step <= questions.length) {
      setName(questions[step-1].name);
    }
  }, [step, questions]);

  const next = () => {
    if (step > questions.length) return;
    userInteractedRef.current = true;
    setStep(step + 1);
  };

  return <Stack component={Card} spacing={2}>
    { step <= questions.length ? <>
    
      <p className="text-info">{props.lesson.headline}</p>
      <h3>{questions[step-1].question}</h3>

      <>
        { questions[step-1].type === "radio" ? <FormControl>
          <RadioGroup name={name}>
            {questions[step-1].options.map((option, index) => {
              const value = questions[step-1].options.length - index;
              return <FormControlLabel key={`${name}_${value}`}
                                      value={value} 
                                      control={<Radio checked={scores[name!] === value} />}
                                      label={option} 
                                      onChange={() => {
                                        setScores(current => { return {...current, [name!]: value }} );
                                        next();
                                      }} />
            })}
          </RadioGroup>
        </FormControl> : questions[step-1].type === "text" ? <>
          <FilledInput 
            value={scores[name!] ?? ""}
            onChange={e => setScores(current => { return {...current, [name!]: e.target.value }})} />

          <Box display="flex" justifyContent="flex-end">
            <Button variant="outlined" 
                    color={scores[name!] ? "primary" : "secondary"}
                    onClick={next}>{scores[name!] ? "Next" : "Skip for now"}</Button>
          </Box>
        </> : questions[step-1].type === "time" ? <>
          <FilledInput 
            value={scores[name!] ?? ""}
            onChange={e => setScores(current => { return {...current, [name!]: e.target.value }})}
            type="number" 
            startAdornment={<AccessTimeIcon color="secondary" />}
            endAdornment={<FormLabel>minutes</FormLabel>} />
        </> : <></> }
      </>
    </> : 
    <h3><TaskAltIcon color="info" sx={{verticalAlign: "-0.25rem"}} /> Questionaire completed</h3>
    }

    <Divider />

    <Stack direction="row" spacing={8} alignItems="center">
      <Stack direction="row" spacing={2} flexGrow={1} alignItems="center">
        { props.multiple !== true && <LinearProgress sx={{flexGrow: 1}} color="info" variant="determinate" value={(step-1)/questions.length*100} /> }
        <FormLabel>Question {step}/{questions.length + (!props.persisted ? 1 : 0)}</FormLabel>
      </Stack>

      <ButtonGroup>
        <IconButton disabled={step <= 1} onClick={() => setStep(step-1)}><NavigateBeforeIcon /></IconButton>
        <IconButton disabled={(step < questions.length && !scores[name!] && questions[step-1].required) || step > questions.length} onClick={next}><NavigateNextIcon /></IconButton>
      </ButtonGroup>
    </Stack>
  </Stack>
};

export default Questionaire;